/**
 * Inbox impersonation — pure shaping for the "mirror teammates' inboxes" bar +
 * sections on `/inbox/cases`. Framework-free so the card mapping, the
 * MULTI-select toggle, the saved-selection migration and the per-teammate
 * grouping are unit-tested; the composable owns the poll + storage, the
 * components own the render.
 *
 * MULTI-SELECT (Sandy 2026-09-17): the viewer can mirror more than one teammate
 * at once and the page stacks one teal section per teammate. Each card is
 * stamped with its `targetId` so the "Reviewed" write knows which teammate's
 * mirror it clears.
 */
import { pluralize, sumBy } from './inboxPage'

/**
 * A server `ChatUnreadCase` (from `getImpersonatedCaseInbox(es)`) → the entry
 * shape `InboxEntityCard` renders, stamped with the teammate it belongs to.
 * Impersonation is MESSAGES-only for now, so `notes` is zeroed and the card
 * speaks for its message count.
 */
export function toImpersonationCard(c, target = null) {
  return {
    kind: 'case',
    entityId: c.caseId,
    name: c.title || null,
    companyId: c.companyId ?? null,
    agencyId: c.agencyId ?? null,
    companyLabel: c.companyNavBarTitle || c.companyShortName || null,
    messages: {
      count: c.count ?? 0,
      latestAt: c.latestAt ?? null,
      snippet: c.snippet ?? null,
    },
    notes: { count: 0 },
    latestAt: c.latestAt ?? null,
    assignedToLabel: c.assignedToLabel ?? null,
    assignedToColor: c.assignedToColor ?? null,
    outcome: c.outcome ?? null,

    // Which mirrored teammate this card belongs to — the "Reviewed" write is
    // keyed by (viewer, target, case), so a card in a multi-select mirror must
    // carry its own target.
    targetId: target?.id != null ? String(target.id) : null,
    targetLabel: target?.label ?? null,

    // Whether the viewer already marked this one Reviewed (today) — the "All"
    // lens shows these, "Unattended" hides them (Sandy 2026-09-17).
    reviewed: c.reviewed ?? false,
  }
}

/**
 * A server `StaffUnreadEntry` (from `getImpersonatedStaffInboxes`) → the entry
 * `InboxEntityCard` renders for a STAFF card (Phase 2), stamped with its
 * teammate. Messages-only, so `notes` is zeroed; the agency reads where a case
 * card shows its company (`companyLabel`).
 */
export function toStaffImpersonationCard(s, target = null) {
  return {
    kind: 'staff',
    entityId: s.staffId,
    name: s.name || null,
    companyLabel: s.agencyLabel ?? null,
    agencyId: s.agencyId ?? null,
    category: s.category ?? null,
    phone: s.phone ?? null,
    messages: {
      count: s.count ?? 0,
      latestAt: s.latestAt ?? null,
      snippet: s.snippet ?? null,
    },
    notes: { count: 0 },
    latestAt: s.latestAt ?? null,

    targetId: target?.id != null ? String(target.id) : null,
    targetLabel: target?.label ?? null,
    reviewed: s.reviewed ?? false,
  }
}

/**
 * A server `ContactUnreadEntry` (from `getImpersonatedContactInboxes`) → the
 * entry `InboxEntityCard` renders for a CONTACT card (Phase 3), stamped with its
 * teammate. Messages-only, so `notes` is zeroed.
 */
export function toContactImpersonationCard(c, target = null) {
  return {
    kind: 'contact',
    entityId: c.contactId,
    name: c.name || null,
    category: c.category ?? null,
    messages: {
      count: c.count ?? 0,
      latestAt: c.latestAt ?? null,
      snippet: c.snippet ?? null,
    },
    notes: { count: 0 },
    latestAt: c.latestAt ?? null,

    targetId: target?.id != null ? String(target.id) : null,
    targetLabel: target?.label ?? null,
    reviewed: c.reviewed ?? false,
  }
}

/**
 * A server `MirroredMissedEntry` (from `getImpersonatedMissedInboxes`) → the
 * entry `InboxCallCard` renders (Phase 4). The server already shapes the caller;
 * we only stamp its teammate + the reviewed flag. The Reviewed write is keyed by
 * the caller's `contactId`, not `entityId` (which is the matched case/staff/contact).
 */
export function toMissedImpersonationCard(c, target = null) {
  return {
    ...c,
    targetId: target?.id != null ? String(target.id) : null,
    targetLabel: target?.label ?? null,
    reviewed: c.reviewed ?? false,
  }
}

/** The still-pending mirrored cards — what the "Unattended" lens shows. */
export function impersonationUnattended(cases) {
  return (cases ?? []).filter(c => !c?.reviewed)
}

/**
 * The rows a mirror section shows under its lens: `'all'` → everything the
 * server returned (pending + today's Reviewed); anything else → only the pending
 * ones. Mirrors the Missed Calls Unattended|All lens.
 */
export function impersonationLensRows(cases, lens) {
  return lens === 'all' ? (cases ?? []) : impersonationUnattended(cases)
}

/**
 * The messages-surface spec (cases / staff / contacts) — the only thing that
 * varies between the three is the entity `noun` in the summary. Fed to
 * `buildImpersonationSection`.
 */
export const entityImpersonationSpec = noun => ({
  noun,
  unit: 'Message',
  countOf: r => r.messages?.count ?? 0,
  titleSuffix: ' - New Messages',
  signal: 'messages',
})

/**
 * The Missed-Calls spec — caller/call nouns, no `signal`, and blanked at zero
 * (that block stays put when its queue empties, so a "0 Callers / 0 Calls" chip
 * would be a count of nothing left to do — Sandy 2026-08-27).
 */
export const CALL_IMPERSONATION_SPEC = {
  noun: 'Caller',
  unit: 'Call',
  countOf: r => r.count,
  titleSuffix: ' Missed Calls',
  blankAtZero: true,
}

/**
 * Build ONE teammate-mirror section from an impersonation group under a lens —
 * the shape all four inbox surfaces stack below their own blocks. The page hands
 * a `spec` (see the two above) saying what its surface counts; everything else
 * is identical, which is the whole point of pulling it here.
 *
 * The summary counts the PENDING rows ("what's left to review"), NEVER the lens
 * rows — so switching to All to see today's reviewed cards doesn't change the
 * number. `render` follows whether the teammate has ANYTHING today (pending or
 * reviewed), so the lens toggle stays reachable once everything is reviewed.
 */
export function buildImpersonationSection(group, lens, spec) {
  const { noun, unit, countOf, titleSuffix, signal = null, blankAtZero = false } = spec
  const pending = impersonationUnattended(group.cases)
  const rows = impersonationLensRows(group.cases, lens)
  const total = group.cases?.length ?? 0

  const summary
    = blankAtZero && !pending.length
      ? ''
      : `${pluralize(pending.length, noun)} / ${pluralize(sumBy(pending, countOf), unit)}`

  const section = {
    key: `impersonation:${group.targetId}`,
    targetId: group.targetId,
    title: `${group.label || 'Teammate'}${titleSuffix}`,
    scope: lens,
    rows,
    collapsed: false,
    summary,
    options: [
      { value: 'unattended', label: 'Unattended', count: pending.length },
      { value: 'all', label: 'All', count: total },
    ],
    render: total > 0,
  }

  // The entity surfaces carry a signal on the section; Missed Calls doesn't.
  if (signal) section.signal = signal

  return section
}

/**
 * The bar's checkbox label for a mirror-able teammate — the `@handle` (Sandy
 * 2026-09-14: "solo @Elizabeth, como en el card de abajo"), i.e. the same
 * `label` the assignee chip shows; falls back to the name only when missing.
 */
export function impersonationPeerLabel(peer) {
  return (
    peer?.label
    || peer?.name
    || peer?.shortName
    || (peer?.id != null ? `#${peer.id}` : '')
  )
}

/** Is this teammate currently mirrored? (ids compared as strings, num/str safe) */
export function isPeerSelected(selectedIds, peerId) {
  const pid = String(peerId ?? '')

  return (selectedIds ?? []).some(id => String(id) === pid)
}

/**
 * The next set of mirrored teammates after clicking one's checkbox: MULTI-select
 * — a peer not mirrored is added, one already mirrored is removed. Returns an
 * array of string ids (order preserved, the newly-added one appended last).
 */
export function nextSelectedTargets(currentIds, peerId) {
  const pid = String(peerId ?? '')
  const list = (currentIds ?? []).map(id => String(id))

  return list.includes(pid) ? list.filter(id => id !== pid) : [...list, pid]
}

/**
 * Which teammates the bar should OFFER: the ones with something to mirror right
 * now (`total > 0`), so the whole bar can hide when nobody has anything (Sandy
 * 2026-09-15). Any currently-SELECTED teammate is ALWAYS kept, even at zero, so
 * a quiet selection can still be un-ticked instead of stranding it. Server
 * (`getImpersonatablePeers`) gives the exact per-peer total. `selectedIds` is the
 * array of mirrored ids (multi-select).
 */
export function visibleImpersonationPeers(peers, selectedIds) {
  const sel = new Set((selectedIds ?? []).map(id => String(id)))

  return (peers ?? []).filter(
    p => (p?.total ?? 0) > 0 || sel.has(String(p?.id)),
  )
}

/**
 * Normalise the persisted selection into the array shape. Accepts the LEGACY
 * single-target object (`{ id, label, color }` from before multi-select) and
 * wraps it, so a saved single mirror survives the upgrade; drops anything with
 * no id.
 */
export function normalizeSavedTargets(saved) {
  const list = Array.isArray(saved) ? saved : saved ? [saved] : []

  return list
    .filter(t => t && t.id != null)
    .map(t => ({
      id: String(t.id),
      label: t.label ?? t.name ?? t.shortName ?? null,
      color: t.color ?? null,
    }))
}

/**
 * Join the server's per-target groups (`getImpersonatedCaseInboxes`) with the
 * viewer's SELECTED targets → one render group per mirrored teammate, in the
 * viewer's selection order, carrying the teammate's label/colour (from the bar's
 * pick) and their mirrored cards (each stamped with `targetId`). A selected
 * teammate the server returned nothing for renders as an empty group.
 */
export function buildImpersonationGroups(serverGroups, targets, opts = {}) {
  // The server field the group's items live under (`cases` / `staff`) and how to
  // shape each one — so the same grouping serves every surface.
  const { itemsKey = 'cases', toCard = toImpersonationCard } = opts

  const byId = new Map(
    (serverGroups ?? []).map(g => [String(g.targetUserId), g]),
  )

  return (targets ?? []).map(t => {
    const g = byId.get(String(t.id))

    return {
      targetId: String(t.id),
      label: t.label ?? null,
      color: t.color ?? null,
      cases: (g?.[itemsKey] ?? []).map(item => toCard(item, t)),
      total: g?.total ?? 0,
    }
  })
}

/** The combined mirror total across every mirrored teammate (the teal badge). */
export function impersonationGrandTotal(groups) {
  return (groups ?? []).reduce((sum, g) => sum + (g?.total ?? 0), 0)
}
